/**
 * What a guest will actually read, for the host who is still editing the draft.
 *
 * The broadcast API fills `{name}`, `{title}`, `{location}` and `{url}` in per
 * guest at send time (OrchestratorAPI `GroupsController.RenderGroupMessage`), so
 * the draft on screen contains the literal text "{url}". A host reading that
 * cannot tell what lands in a guest's inbox — live QA flagged exactly this.
 * This renders the same substitution locally, for one representative guest, so
 * the composer can show the result underneath the editable fields.
 *
 * It mirrors the server's rules and is only ever a PREVIEW: nothing here is
 * sent, and the server's render remains the authority. The rules mirrored:
 * the four tokens, matched case-insensitively, applied to the email body and
 * the SMS body (the subject is not templated), and a per-guest link only when
 * "Give each guest their own link" is on AND the guest has a token — otherwise
 * the shared public link, which is what the API falls back to too.
 */

/** Shown when the group has no guests to borrow a name from. */
export const PLACEHOLDER_GUEST_NAME = 'Alex';

/** Shown in place of a real token when there is no guest to take one from. */
export const PLACEHOLDER_INVITE_TOKEN = 'INVITE-TOKEN';

export interface PreviewGuest {
  fullName?: string | null;
  inviteToken?: string | null;
}

export interface GuestPreviewInput {
  subject: string;
  emailBody: string;
  smsBody: string;
  groupTitle: string;
  groupLocation?: string | null;
  groupSlug: string;
  /** The first guest the message will go to, if the group has any. */
  guest?: PreviewGuest | null;
  personalizeInviteLinks: boolean;
  /** The existing public URL builder (`groupPublicUrl`); injected to keep this pure. */
  publicUrl: (slug: string, inviteToken?: string) => string;
}

export interface GuestPreview {
  subject: string;
  email: string;
  sms: string;
  guestName: string;
  /** True when no guest was available and the name is the stand-in. */
  guestNameIsPlaceholder: boolean;
  url: string;
  /** True when {url} carries an invite token — every guest gets a different one. */
  urlIsPerGuest: boolean;
}

/** The four variables, replaced the way the API replaces them: in order, ignoring case. */
export function renderGuestVariables(
  template: string,
  values: { name: string; title: string; location: string; url: string },
): string {
  const replaceAll = (text: string, token: string, value: string) =>
    text.replace(new RegExp(token.replace(/[{}]/g, '\\$&'), 'gi'), () => value);
  let out = replaceAll(template, '{name}', values.name);
  out = replaceAll(out, '{title}', values.title);
  out = replaceAll(out, '{location}', values.location);
  out = replaceAll(out, '{url}', values.url);
  return out;
}

export function buildGuestPreview(input: GuestPreviewInput): GuestPreview {
  const realName = input.guest?.fullName?.trim() || '';
  const guestName = realName || PLACEHOLDER_GUEST_NAME;

  // Same rule as the API's GuestUrl(): a personal link needs the toggle AND a
  // token; without either it is the shared link.
  const token = input.personalizeInviteLinks
    ? (input.guest?.inviteToken?.trim() || (input.guest ? '' : PLACEHOLDER_INVITE_TOKEN))
    : '';
  const url = input.publicUrl(input.groupSlug, token || undefined);

  const values = {
    name: guestName,
    title: input.groupTitle,
    location: input.groupLocation ?? '',
    url,
  };
  return {
    subject: input.subject,
    email: renderGuestVariables(input.emailBody, values),
    sms: renderGuestVariables(input.smsBody, values),
    guestName,
    guestNameIsPlaceholder: !realName,
    url,
    urlIsPerGuest: Boolean(token),
  };
}
